import React, { useState, useEffect } from 'react';
import { AlertTriangle, Download, Lock, Trash2 } from 'lucide-react';
import { useAuth } from '../App';
import api from '../services/api';
import toast from 'react-hot-toast';
import PageHeader from '../components/PageHeader';

export default function Settings() {
  const { user, partner, refreshPartner } = useAuth();
  const [form, setForm] = useState({ company_name: partner?.company_name||'', company_address: partner?.company_address||'', tax_id: partner?.tax_id||'' });
  const [saving, setSaving] = useState(false);
  const incomplete = !partner?.company_address || !partner?.tax_id;
  // ⑮ Vertrag & Konto: Kündigungs-Status frisch vom Server (nicht aus dem Login-Cache).
  const [deletionRequestedAt, setDeletionRequestedAt] = useState(partner?.deletion_requested_at || null);
  const [deleting, setDeleting] = useState(false);
  // Plattform-Stammdaten aus der zentralen Quelle (GELD-UND-BELEGE-001) statt hart kodiert;
  // die USt-IdNr. liefert der Server nur, wenn sie wirklich gepflegt ist.
  const [platformIdent, setPlatformIdent] = useState(null);
  useEffect(() => {
    api.get('/partners/me').then(r => setDeletionRequestedAt(r.data.partner?.deletion_requested_at || null)).catch(() => {});
    api.get('/legal/platform').then(r => setPlatformIdent(r.data.platform)).catch(() => {});
  }, []);

  const requestDeletion = async () => {
    const ok = window.confirm('Konto-Löschung anfragen?\n\nDas ist die Kündigung gemäß Partner-Vertrag § 8. Die Vermittlung wird SOFORT gestoppt und deine Fahrer gehen offline. Offene Beträge werden abgerechnet, danach wird das Konto deaktiviert.\n\nDu kannst die Anfrage zurückziehen, solange die Abwicklung nicht abgeschlossen ist.');
    if (!ok) return;
    setDeleting(true);
    try {
      await api.post('/partners/deletion-request');
      setDeletionRequestedAt(new Date().toISOString());
      toast.success('Kündigung eingereicht — Vermittlung gestoppt');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Anfrage fehlgeschlagen');
    } finally { setDeleting(false); }
  };

  const withdrawDeletion = async () => {
    setDeleting(true);
    try {
      await api.delete('/partners/deletion-request');
      setDeletionRequestedAt(null);
      toast.success('Anfrage zurückgezogen — Vermittlung läuft wieder an');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Zurückziehen fehlgeschlagen');
    } finally { setDeleting(false); }
  };

  const inp = { width: '100%', padding: '11px 13px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, color: '#111', marginBottom: 14 };

  const save = async () => {
    if (form.company_address.trim().length < 6) return toast.error('Bitte vollständige Firmenanschrift angeben (Straße, PLZ, Ort)');
    if (form.tax_id.trim().length < 5) return toast.error('Bitte Steuernummer oder USt-IdNr. angeben');
    setSaving(true);
    try {
      await api.patch('/partners/me/profile', form);
      await refreshPartner?.();
      toast.success('Firmendaten gespeichert');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Einstellungen" description="Firmenprofil und Kontodaten" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Firmendaten */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
          <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 16 }}>Firmendaten</h3>
          {incomplete && (
            <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, marginBottom: 16, fontSize: 12, color: '#991b1b', lineHeight: 1.5 }}>
              <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> <strong>Anschrift und Steuernummer fehlen.</strong> Diese sind für rechtskonforme Rechnungen (§ 14 UStG) erforderlich – bitte ergänzen.
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <div style={{ width: 56, height: 56, borderRadius: 12, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22, fontWeight: 900, flexShrink: 0 }}>
              {partner?.company_name?.charAt(0) || 'U'}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>{partner?.company_name}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                {partner?.approval_status === 'approved' ? 'Freigegeben' : partner?.approval_status === 'pending' ? 'In Prüfung' : 'Abgelehnt'}
              </div>
            </div>
          </div>
          {[
            { label: 'Firmenname *', key: 'company_name', ph: 'Mustermann Transporte GmbH' },
            { label: 'Firmenanschrift * (Straße, PLZ, Ort)', key: 'company_address', ph: 'Musterstraße 1, 10115 Berlin' },
            { label: 'Steuernummer / USt-IdNr. *', key: 'tax_id', ph: 'DE123456789' },
          ].map(({ label, key, ph }) => (
            <div key={key}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>{label}</label>
              <input value={form[key]} placeholder={ph} onChange={e => setForm({...form, [key]: e.target.value})}
                style={inp} onFocus={e => e.target.style.borderColor='#000'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />
            </div>
          ))}
          <button onClick={save} disabled={saving}
            style={{ padding: '10px 20px', background: saving ? '#e5e7eb' : '#000', color: saving ? '#6b7280' : '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>

        {/* Kontodaten */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
          <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 16 }}>Kontodaten</h3>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>Name</label>
            <div style={{ padding: '11px 13px', background: '#f9fafb', borderRadius: 8, fontSize: 14 }}>{user?.name}</div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>E-Mail</label>
            <div style={{ padding: '11px 13px', background: '#f9fafb', borderRadius: 8, fontSize: 14 }}>{user?.email}</div>
          </div>
          <div style={{ padding: 14, background: '#faf5fb', borderRadius: 10, border: '1px solid #F3E5F5', marginTop: 16 }}>
            <p style={{ fontSize: 12, color: '#7B1FA2', lineHeight: 1.5 }}>
              <Lock size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> <strong>Partner-Portal</strong> · Lizenzierter Mietwagenverkehr gem. § 49 PBefG<br/>
              {platformIdent?.name || 'VooGo UG (haftungsbeschränkt)'}{platformIdent?.vat_id ? ` · USt-IdNr. ${platformIdent.vat_id}` : ''}
            </p>
          </div>
        </div>
      </div>

      {/* ⑮/⑯ Vertrag & Konto: DSGVO-Export + Löschung anfragen (= Kündigung, Partner-Vertrag § 8) */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: deletionRequestedAt ? '1px solid #fca5a5' : '1px solid #e5e7eb', marginTop: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>Vertrag & Konto</h3>
        <button onClick={async () => {
          try {
            const r = await api.get('/auth/me/export');
            const url = window.URL.createObjectURL(new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' }));
            const a = document.createElement('a'); a.href = url; a.download = `VOOGO-Datenexport-${new Date().toISOString().slice(0, 10)}.json`; a.click();
            window.URL.revokeObjectURL(url);
          } catch { toast.error('Export fehlgeschlagen'); }
        }} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 20px', background: '#f3f4f6', color: '#111', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', marginBottom: 14 }}>
          <Download size={14} /> Meine Daten exportieren (DSGVO)
        </button>
        {deletionRequestedAt ? (
          <>
            <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, marginBottom: 14, fontSize: 13, color: '#991b1b', lineHeight: 1.6 }}>
              <strong>Kündigung eingereicht</strong> — die Vermittlung ist gestoppt, deine Fahrer können nicht online gehen.
              VOOGO rechnet die offenen Beträge ab; danach wird das Konto deaktiviert und deine Zugangsdaten werden anonymisiert.
              Belege und das Auftragsbuch bleiben gemäß der gesetzlichen Aufbewahrungsfristen erhalten.
            </div>
            <button onClick={withdrawDeletion} disabled={deleting}
              style={{ padding: '10px 20px', background: '#f3f4f6', color: '#111', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', opacity: deleting ? 0.6 : 1 }}>
              Anfrage zurückziehen
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.6, marginBottom: 14 }}>
              Die Anfrage zur Konto-Löschung gilt als <strong>Kündigung gemäß Partner-Vertrag § 8</strong>.
              Die Vermittlung wird sofort gestoppt; offene Beträge werden abgerechnet, danach wird das Konto
              deaktiviert und deine Zugangsdaten werden anonymisiert. Belege und das Auftragsbuch bleiben
              gemäß gesetzlicher Aufbewahrungsfristen (u. a. § 147 AO, § 49 PBefG) erhalten.
            </p>
            <button onClick={requestDeletion} disabled={deleting}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 20px', background: '#fef2f2', color: '#dc2626', borderRadius: 8, fontSize: 13, fontWeight: 700, border: '1px solid #fecaca', cursor: 'pointer', opacity: deleting ? 0.6 : 1 }}>
              <Trash2 size={14} /> Konto-Löschung anfragen (Kündigung)
            </button>
          </>
        )}
      </div>
    </div>
  );
}
